import { useCallback, useEffect, useState } from "react";

import { Button, Drawer, Input, Label, Radio } from "@components";

import { FlowAPI } from "@providers/FlowAPI";
import styles from "@styles/containers/flows/CreateFolderDrawer.module.scss";
import debounce from "lodash.debounce";
import { OlgaEnvironmentsAPI } from "../../../../../providers/OlgaEnvironmentsAPI";

const steps = {
  REMOVE_FOLDER: "REMOVE_FOLDER",
  MOVE_FLOWS: "MOVE_FLOWS",
};

export default function RemoveFolderDrawer({
  visible,
  folder,
  onClose,
  onBack,
  onLoadFlows,
  onLoadFolders,
}) {
  const [step, setStep] = useState(steps.REMOVE_FOLDER);
  const [folders, setFolders] = useState([]);
  const [destinationFolder, setDestinationFolder] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [archiving, setArchiving] = useState(false);
  const [moving, setMoving] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  useEffect(() => {
    if (visible) loadFolders();
  }, [visible]);

  const loadFolders = async () => {
    const folders = await getFolders();
    setFolders(folders);
  };

  const getFolders = async () => {
    const environmentConfigurations =
      await OlgaEnvironmentsAPI.getConfigurations();
    return environmentConfigurations.getData({})?.flow_folders || [];
  };

  const archiveFolder = async () => {
    setArchiving(true);

    const deleteResponse = await FlowAPI.deleteFolder(folder.id);
    if (deleteResponse.getErrors([]).length > 0) {
      alert({ text: deleteResponse.getErrors()[0] });
      return setArchiving(false);
    }

    setArchiving(false);
    onBack();
    onLoadFolders();
    setStep(steps.REMOVE_FOLDER);
  };

  const moveFolder = async () => {
    setMoving(true);

    const moveResponse = await FlowAPI.moveFolder(
      folder.id,
      destinationFolder.id
    );
    const currentFolders = await getFolders();
    const updatedFolders = currentFolders.filter(
      (existingFolder) => existingFolder.id != folder.id
    );

    await OlgaEnvironmentsAPI.putConfigurations({
      flow_folders: updatedFolders,
    });
    if (moveResponse.getErrors([]).length > 0) {
      alert({ text: moveResponse.getErrors()[0] });
      return setMoving(false);
    }

    setMoving(false);
    onBack();
    setStep(steps.REMOVE_FOLDER);
    onLoadFlows();
    onLoadFolders();
    loadFolders();
  };

  const unlinkFolder = async () => {
    setUnlinking(true);
    const moveResponse = await FlowAPI.moveFolder(folder.id, null);

    if (moveResponse.getErrors([]).length > 0) {
      alert({ text: moveResponse.getErrors()[0] });
      return setUnlinking(false);
    }

    const currentFolders = await getFolders();
    const updatedFolders = currentFolders.filter(
      (existingFolder) => existingFolder.id != folder.id
    );

    await OlgaEnvironmentsAPI.putConfigurations({
      flow_folders: updatedFolders,
    });

    setUnlinking(false);
    onBack();
    setStep(steps.REMOVE_FOLDER);
    onLoadFlows();
    loadFolders();
    onLoadFolders();
  };

  const filterFolder = () => {
    if (!debouncedSearchTerm) {
      return folders;
    }

    return folders.filter((folder) => {
      const name = folder?.name.toLowerCase();
      const filterName = debouncedSearchTerm.toLowerCase();
      return name.includes(filterName);
    });
  };

  const titles = {
    [steps.REMOVE_FOLDER]: "Excluir pasta",
    [steps.MOVE_FLOWS]: "Mover fluxos",
  };

  const debounceSearchTerm = useCallback(
    debounce((searchTerm) => {
      setDebouncedSearchTerm(searchTerm);
    }, 500),
    []
  );

  const filteredFolders = filterFolder(folders);

  console.log(filteredFolders);

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        setStep(steps.REMOVE_FOLDER);
        onClose();
      }}
      title={titles[step]}
      subtitle={`"${folder?.name}"`}
      showBackButton
      backButton={() => {
        onBack();
        setStep(steps.REMOVE_FOLDER);
      }}
      withoutTransition
    >
      {step === steps.REMOVE_FOLDER ? (
        <>
          <Label>Você gostaria de arquivar os fluxos ou manter?</Label>

          <Button
            className="mb-15 mt-15"
            design="three"
            onClick={() => setStep(steps.MOVE_FLOWS)}
            disabled={archiving}
          >
            Desejo manter os fluxos
          </Button>
          <Button
            design="three"
            onClick={archiveFolder}
            disabled={archiving}
            loading={archiving}
          >
            Desejo arquivar os fluxos
          </Button>
        </>
      ) : null}

      {step === steps.MOVE_FLOWS ? (
        <>
          <Label>
            Para que pasta você gostaria de mover os fluxos de "{folder?.name}"?
          </Label>

          <Input
            search
            value={searchTerm}
            records={filteredFolders.length}
            placeholder="Buscar por pasta..."
            prefix
            prefixWidth={20}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              debounceSearchTerm(e.target.value);
            }}
          />

          <div className={styles.createdFolders}>
            {filteredFolders.map((folder) => (
              <div
                className={styles.folder}
                key={folder.id}
                onClick={() => setDestinationFolder(folder)}
              >
                <Radio
                  family="folder"
                  value={folder.id}
                  checked={destinationFolder ? destinationFolder.id : null}
                >
                  {folder?.name}
                </Radio>
              </div>
            ))}
          </div>

          <div className={styles.footer}>
            {destinationFolder && (
              <Button
                className="w-100 mb-15"
                onClick={moveFolder}
                loading={moving}
                disabled={moving || unlinking}
                design="primary"
              >
                Salvar
              </Button>
            )}

            <Button
              className="w-100"
              design="three"
              onClick={unlinkFolder}
              loading={unlinking}
              disabled={moving || unlinking}
            >
              Não quero agrupar fluxo
            </Button>
          </div>
        </>
      ) : null}
    </Drawer>
  );
}
